import React from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { TopBar } from "@/components/TopBar";
import { useAuth } from "@/context/AuthContext";
import { apiDaftarBatalSpk, apiPutuskanBatalSpk, formatApiErrorDetail } from "@/lib/api";
import * as W from "@/lib/waktu";

// Owner 2026-10-12: the Manager requests an SPK cancellation with a reason; the
// owner approves or refuses here and handles the nota / money with Batal nota
// (menu Nota / Pembayaran). An approval does not cancel anything by itself.
const STATUS = {
  MENUNGGU: "border-amber-500/40 bg-amber-50 text-amber-800",
  DISETUJUI: "border-emerald-500/40 bg-emerald-50 text-emerald-700",
  DITOLAK: "border-slate-400/40 bg-slate-100 text-slate-600",
};

function Putuskan({ p, onDone }) {
  const [catatan, setCatatan] = React.useState("");
  const [err, setErr] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const kirim = async (keputusan) => {
    setBusy(true); setErr(null);
    try { onDone(await apiPutuskanBatalSpk(p.id, keputusan, catatan.trim())); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-2 space-y-2">
      <input data-testid={`sb-catatan-${p.id}`} value={catatan} onChange={(e) => setCatatan(e.target.value)} placeholder="Catatan keputusan (wajib, min. 5 huruf)"
        className="w-full min-h-[40px] rounded-md border border-slate-700 bg-slate-950 px-2.5 text-sm text-slate-100" />
      <div className="flex flex-wrap gap-2">
        <button data-testid={`sb-setuju-${p.id}`} disabled={busy || catatan.trim().length < 5} onClick={() => kirim("SETUJU")}
          className="rounded-md bg-soq-biru px-3 py-1.5 text-sm text-white disabled:opacity-40">Setujui</button>
        <button data-testid={`sb-tolak-${p.id}`} disabled={busy || catatan.trim().length < 5} onClick={() => kirim("TOLAK")}
          className="rounded-md border border-slate-600 px-3 py-1.5 text-sm text-slate-200 disabled:opacity-40">Tolak</button>
      </div>
      {err && <p className="text-sm text-red-600">{err}</p>}
    </div>
  );
}

export default function SpkBatalPage() {
  const { user } = useAuth();
  const owner = user?.role === "admin";
  const [rows, setRows] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const load = React.useCallback(() => {
    apiDaftarBatalSpk().then(setRows).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);
  React.useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="spk-batal-page" className="min-h-screen bg-slate-950 text-slate-200">
      <TopBar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-5">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-100">Pengajuan Batal SPK</h1>
          <p className="text-sm text-slate-400 mt-1">
            {owner
              ? <>Pengajuan dari Manager. Setujui atau tolak; bila disetujui, batalkan notanya lewat menu <Link to="/nota" className="underline text-soq-biru">Nota</Link> (uang & stok diurus di sana).</>
              : "Pengajuan Anda ke Owner. Owner yang memutuskan dan mengurus nota/uang. Ajukan dari menu SPK (tombol di detail SPK)."}
          </p>
        </div>
        {err && <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">{err}</div>}
        {!rows && !err && <div className="flex items-center gap-2 text-slate-400"><Loader2 className="animate-spin" size={18} /> Memuat…</div>}
        {rows && rows.length === 0 && <p data-testid="sb-kosong" className="py-6 text-center text-slate-500">Belum ada pengajuan.</p>}
        <div className="space-y-3">
          {(rows || []).map((p) => (
            <div key={p.id} data-testid={`sb-${p.nomor_spk}`} className="rounded-xl border border-slate-800 bg-white p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="font-semibold text-slate-100">{p.nomor_spk} <span className="font-mono text-xs text-slate-500">{p.nomor_faktur}</span></p>
                  <p className="text-xs text-slate-500">Diajukan {p.diajukan_oleh?.nama || p.diajukan_oleh?.username} · {W.waktu(p.diajukan_pada)}</p>
                </div>
                <span className={`rounded-md border px-2 py-0.5 text-xs font-semibold ${STATUS[p.status]}`}>{p.status}</span>
              </div>
              <p className="mt-2 text-sm text-slate-300"><b>Alasan:</b> {p.alasan}</p>
              {p.keputusan && <p className="mt-1 text-sm text-slate-400"><b>Keputusan</b> {p.keputusan.oleh?.nama || p.keputusan.oleh?.username} · {W.waktu(p.keputusan.pada)}: {p.keputusan.catatan}</p>}
              {owner && p.status === "MENUNGGU" && <Putuskan p={p} onDone={load} />}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
